import { theme as antdTheme } from 'antd'
import { type ThemeColor, themeFrom, themeTokens } from './theme-color'
import { type ThemeMode, fontSize, isHexColor, lineHeight, radius, readableOn } from './tokens'

const { defaultAlgorithm, darkAlgorithm, compactAlgorithm } = antdTheme

/**
 * Các override trong `themeTokens` giả định nền sáng: nền nút trắng, nền mục menu
 * là màu nhạt của brand. Giữ nguyên ở dark mode thì ra trắng-trên-trắng (nút mặc định)
 * hoặc chữ sáng trên nền sáng (menu). Ở dark ta bỏ hẳn các key này để `darkAlgorithm`
 * tự tính — chỉ bỏ màu phụ thuộc nền, các key kích thước / bo góc giữ nguyên.
 */
const LIGHT_SURFACE_OVERRIDES: Record<string, string[]> = {
  // colorPrimary* của Button/Pagination/... được dựng từ bảng màu SÁNG (hover = sắc đậm hơn).
  // Giữ nguyên ở dark thì nút không đổi theo theme và hover tối đi thay vì sáng lên;
  // bỏ ra để `darkAlgorithm` tự suy từ `token.colorPrimary`.
  Button: ['defaultBg', 'colorPrimary', 'colorPrimaryHover', 'colorPrimaryActive'],
  Menu: [
    'itemActiveBg',
    'itemSelectedBg',
    'itemSelectedColor',
    'subMenuItemSelectedColor',
    'colorPrimaryBorder',
  ],
  Table: ['rowHoverBg', 'colorPrimaryBorder'],
  Pagination: ['itemActiveBg', 'colorPrimary'],
  Collapse: ['headerBg'],
}

/**
 * Menu `theme="dark"` (MenuLeft / header truyền theo mode) đọc token `dark*` riêng: mặc định
 * antd cho nền xanh navy cố định (darkItemBg) và nền mục đang chọn = colorPrimary đặc.
 * Trong app, Sider/Header đã có nền surface → menu phải trong suốt, mục chọn/hover là
 * lớp mờ theo semantic. Đây là nguồn "menu dark ra màu xanh".
 */
const darkMenuTokens = (primary: string) => ({
  darkItemBg: 'transparent',
  darkSubMenuItemBg: 'transparent',
  darkPopupBg: 'var(--vbdt-color-bg-surface)',
  darkItemColor: 'var(--vbdt-color-text-secondary)',
  darkItemHoverColor: 'var(--vbdt-color-text-primary)',
  darkItemHoverBg: 'var(--vbdt-color-bg-hover)',
  darkItemSelectedBg: `color-mix(in srgb, ${primary} 24%, transparent)`,
  darkItemSelectedColor: 'var(--vbdt-color-text-primary)',
  darkGroupTitleColor: 'var(--vbdt-color-text-secondary)',
})

const dropLightSurfaceOverrides = (components: Record<string, any>) => {
  const out: Record<string, any> = { ...components }
  for (const [component, keys] of Object.entries(LIGHT_SURFACE_OVERRIDES)) {
    if (!out[component]) continue
    const rest = { ...out[component] }
    for (const key of keys) delete rest[key]
    out[component] = rest
  }
  return out
}

/** Tuỳ chọn hình khối lấy từ Thiết lập bố cục. Bỏ trống = giữ nguyên theme gốc. */
export type AntdShapeOptions = {
  /** Bo góc cơ sở (px). Mặc định `radius.md`. */
  radius?: number
  /** Mật độ gọn: hạ chiều cao control và khoảng đệm ô bảng. */
  compact?: boolean
  /** Màu chủ đạo tuỳ chọn `#RRGGBB`; có giá trị thì thay Theme Color. */
  customPrimary?: string
  /** Bộ nền theo preset/màu chủ đạo: ghi đè nền thẻ, popover, sider, header của AntD. */
  surfaces?: { page: string; surface: string; sunken: string }
}

/** Ghi đè bo góc / mật độ lên bản sao của `components`. Mặc định thì trả nguyên. */
const applyShape = (components: Record<string, any>, { radius: r, compact }: AntdShapeOptions) => {
  const out: Record<string, any> = { ...components }
  const patch = (name: string, values: Record<string, unknown>) => {
    out[name] = { ...out[name], ...values }
  }

  if (r !== undefined && r !== radius.md) {
    const small = Math.max(2, r - 2)
    patch('Button', { borderRadius: r, borderRadiusLG: r, borderRadiusSM: small })
    patch('Table', { borderRadius: r, headerBorderRadius: r })
    patch('Card', { borderRadiusLG: r })
    patch('Collapse', { borderRadiusLG: r })
    patch('Menu', { itemBorderRadius: r })
  }

  if (compact) {
    const heights = { controlHeight: 32, controlHeightLG: 40, controlHeightSM: 24 }
    patch('Button', { ...heights, paddingInline: 12 })
    patch('Input', heights)
    patch('Select', heights)
    patch('DatePicker', heights)
    patch('Table', { cellPaddingBlock: 8, cellPaddingInline: 12 })
    patch('Menu', { itemHeight: 36 })
  }
  return out
}

/**
 * Dựng object theme cho ConfigProvider.
 * `cssVar: true` để AntD sinh biến CSS thay vì lớp có độ ưu tiên cao —
 * nhờ đó đè style bằng biến, không phải bằng !important.
 * `hashed: false` để tên lớp ngắn, dễ đọc khi phải đè ở tầng 4.
 */
export const buildAntdTheme = (
  mode: ThemeMode,
  themeColor: ThemeColor = 'primary',
  shape: AntdShapeOptions = {}
) => {
  const custom = isHexColor(shape.customPrimary) ? shape.customPrimary : undefined
  const legacy = custom ? themeFrom(custom) : themeTokens(themeColor)
  const base =
    mode === 'dark' ? dropLightSurfaceOverrides(legacy.components) : { ...legacy.components }
  if (mode === 'dark') base.Menu = { ...base.Menu, ...darkMenuTokens(legacy.colorPrimary) }
  const components =
    shape.compact || (shape.radius !== undefined && shape.radius !== radius.md)
      ? applyShape(base, shape)
      : base
  const surfaces = custom ? shape.surfaces : undefined
  if (surfaces) {
    base.Layout = {
      ...base.Layout,
      siderBg: surfaces.surface,
      headerBg: surfaces.surface,
      bodyBg: surfaces.page,
      triggerBg: surfaces.sunken,
    }
  }
  const modeAlgorithm = mode === 'dark' ? darkAlgorithm : defaultAlgorithm

  return {
    cssVar: { prefix: 'ant' },
    hashed: false,
    algorithm: shape.compact ? [modeAlgorithm, compactAlgorithm] : modeAlgorithm,
    token: {
      colorPrimary: legacy.colorPrimary,
      colorInfo: legacy.colorInfo,
      // Chữ trên nút primary: trắng hoặc đen tuỳ màu custom đậm hay nhạt.
      ...(custom ? { colorTextLightSolid: readableOn(custom) } : {}),
      ...(surfaces
        ? { colorBgContainer: surfaces.surface, colorBgElevated: surfaces.surface }
        : {}),
      colorBgLayout: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
      controlHeight: shape.compact ? 32 : 40,
      fontSize: fontSize.base,
      lineHeight: lineHeight.base / fontSize.base,
      borderRadius: shape.radius ?? radius.md,
      fontFamily:
        '"Be Vietnam Pro", "Inter", ui-sans-serif, "Segoe UI Emoji", "Noto Color Emoji", Helvetica, sans-serif',
    },
    components,
  }
}
